import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Stack,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import React, { useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const CopyableId = ({ label, value, accent }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
        gap: 1,
      }}
    >
      <Box minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          letterSpacing={0.5}
          display="block"
        >
          {label}
        </Typography>
        <Tooltip title={value} placement="top">
          <Typography
            variant="body2"
            fontWeight={700}
            fontFamily="monospace"
            noWrap
            sx={{ color: accent, fontSize: 13 }}
          >
            {value}
          </Typography>
        </Tooltip>
      </Box>

      <Tooltip title={copied ? "Copied!" : "Copy ID"} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            flexShrink: 0,
            color: copied ? "#1a7f4b" : "text.secondary",
            bgcolor: copied ? "#e6f9f0" : "transparent",
            transition: "all 0.2s",
            "&:hover": { bgcolor: `${accent}1A`, color: accent },
          }}
        >
          {copied ? (
            <DoneIcon sx={{ fontSize: 15 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 15 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const TemplateCard = ({ item, index }) => {
  const colors = ["#6C63FF", "#FF6584", "#43C6AC", "#F7971E"];
  const accent = colors[index % colors.length];

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "visible",
        position: "relative",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 8px 24px ${accent}33`,
        },
      }}
    >
      {/* Accent bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 4,
          borderRadius: "12px 12px 0 0",
          background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
        }}
      />

      <CardContent sx={{ pt: 3, pb: "16px !important" }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: `${accent}1A`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18, color: accent }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Template Group
            </Typography>
          </Stack>

          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
            label={`${item.successfulAccounts}/${item.totalAccounts} OK`}
            size="small"
            sx={{
              bgcolor: "#e6f9f0",
              color: "#1a7f4b",
              fontWeight: 600,
              fontSize: 11,
              "& .MuiChip-icon": { color: "#1a7f4b" },
            }}
          />
        </Stack>

        {/* Root Template ID */}
        <CopyableId
          label="ROOT TEMPLATE ID"
          value={item.rootTemplateId}
          accent={accent}
        />

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            MAPPED TO
          </Typography>
        </Divider>

        {/* Mapped Template IDs */}
        <Stack gap={1.5}>
          {item.mappedTemplates.map((mt, i) => (
            <Box key={mt.templateId}>
              <CopyableId
                label={`MAPPED TEMPLATE ID ${
                  item.mappedTemplates.length > 1 ? i + 1 : ""
                }`}
                value={mt.templateId}
                accent={accent}
              />
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ pl: 0.5, mt: 0.5, display: "block" }}
                noWrap
              >
                {mt.accountId}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Footer */}
        <Stack direction="row" alignItems="center" gap={0.5}>
          <CalendarTodayIcon sx={{ fontSize: 12, color: "text.disabled" }} />
          <Typography variant="caption" color="text.disabled">
            {formatDate(item.createdOn)}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const GroupedTemplates = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/api/mapped-templates-list`
  );

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <BackBtn />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Grouped Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.length ?? 0} mapping{data?.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>
        <Chip
          label={data?.[0]?.rootAccountId ?? ""}
          size="small"
          sx={{
            fontWeight: 600,
            bgcolor: "#f0f0ff",
            color: "#6C63FF",
            maxWidth: 280,
          }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 3,
        }}
      >
        {data?.map((item, i) => (
          <TemplateCard key={item._id} item={item} index={i} />
        ))}
      </Box>
    </Container>
  );
};

export default GroupedTemplates;
